import {
	faChevronDown,
	faPlus,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Z_INDEX } from '../../../../constants/zIndex';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { SortField } from '../../../types';
import { DraggableList, DragHandle } from '../../shared/DraggableList';

interface SortFieldsListProps {
	sortFields: SortField[];
	availableFields: Array<{
		fieldName: string;
		fieldLabel: string;
		objectTable: string;
		objectLabel: string;
	}>;
	onUpdate: (sortFields: SortField[]) => void;
	showAddButton?: boolean;
}

export function SortFieldsList({
	sortFields,
	availableFields,
	onUpdate,
	showAddButton = true,
}: SortFieldsListProps) {
	const colors = useThemeColors();
	const [openFieldDropdown, setOpenFieldDropdown] = useState<string | null>(
		null,
	);

	// Get available fields excluding already selected ones
	const getAvailableFieldsForIndex = (currentIndex: number) => {
		const usedFields = sortFields
			.map((field, index) =>
				index !== currentIndex
					? `${field.objectTable}.${field.fieldName}`
					: null,
			)
			.filter(Boolean);

		return availableFields.filter(
			(field) =>
				!usedFields.includes(`${field.objectTable}.${field.fieldName}`),
		);
	};

	const addSortField = () => {
		const availableForNew = getAvailableFieldsForIndex(-1);
		const firstAvailable = availableForNew[0];
		if (firstAvailable == null) return;

		const newSortField: SortField = {
			objectTable: firstAvailable.objectTable,
			fieldName: firstAvailable.fieldName,
			direction: 'ASC',
		};

		onUpdate([...sortFields, newSortField]);
	};

	const updateSortField = (index: number, updates: Partial<SortField>) => {
		const updatedSortFields = [...sortFields];
		const current = updatedSortFields[index];
		if (current == null) {
			return;
		}
		updatedSortFields[index] = { ...current, ...updates };
		onUpdate(updatedSortFields);
	};

	const deleteSortField = (index: number) => {
		const updatedSortFields = sortFields.filter((_, i) => i !== index);
		onUpdate(updatedSortFields);
	};

	const reorderSortFields = (reorderedFields: SortField[]) => {
		onUpdate(reorderedFields);
	};

	return (
		<div>
			{showAddButton && (
				<div className="flex justify-between items-center mb-4">
					<h3 className={`text-sm font-semibold ${colors.classes.textPrimary}`}>
						Sort Fields ({sortFields.length})
					</h3>
					<button
						onClick={addSortField}
						disabled={getAvailableFieldsForIndex(-1).length === 0}
						className={`
              flex items-center gap-1 py-1.5 px-2.5 rounded text-xs font-medium
              ${
								getAvailableFieldsForIndex(-1).length > 0
									? `${colors.classes.bgPrimary} text-white hover:${colors.classes.bgPrimaryHover}`
									: `${colors.classes.bgSecondary} ${colors.classes.textSecondary} cursor-not-allowed`
							}
            `}
					>
						<Icon icon={faPlus} size="xs" />
						<span>Add Field</span>
					</button>
				</div>
			)}

			<DraggableList
				data={sortFields}
				keyExtractor={(_, index) => `sort_${index}`}
				renderItem={(sortField, index, isDragging) => (
					<div
						className={`
            flex items-center p-3 ${colors.classes.bgPrimary} border rounded-lg mb-2 gap-2
            ${isDragging ? colors.classes.bgSecondary : ''}
            ${colors.classes.borderDefault}
          `}
					>
						<DragHandle />

						{/* Field Dropdown */}
						<div className="flex-1 relative">
							<button
								onClick={() =>
									setOpenFieldDropdown(
										openFieldDropdown === `field_${index}`
											? null
											: `field_${index}`,
									)
								}
								className={`flex items-center w-full border ${colors.classes.borderDefault} rounded px-2 py-1.5 ${colors.classes.bgPrimary} ${colors.classes.bgPrimaryHover}`}
							>
								<span
									className={`
                  flex-1 text-xs text-left mr-1.5
                  ${sortField.fieldName ? colors.classes.textPrimary : colors.classes.textSecondary}
                `}
								>
									{sortField.fieldName
										? (() => {
												const foundField = availableFields.find(
													(f) =>
														f.objectTable === sortField.objectTable &&
														f.fieldName === sortField.fieldName,
												);
												return foundField
													? `${foundField.objectLabel}: ${foundField.fieldLabel}`
													: `${sortField.objectTable}.${sortField.fieldName}`;
											})()
										: 'Select field...'}
								</span>
								<Icon
									icon={faChevronDown}
									size="xs"
									color={colors.values.textMuted}
								/>
							</button>

							{/* Field Dropdown Menu */}
							{openFieldDropdown === `field_${index}` && (
								<div
									className={`absolute top-full left-0 right-0 ${colors.classes.bgPrimary} border ${colors.classes.borderDefault} rounded mt-1 max-h-[200px] overflow-y-auto shadow-lg`}
									style={{ zIndex: Z_INDEX.dropdown }}
								>
									{(() => {
										const availableForIndex = getAvailableFieldsForIndex(index);
										return availableForIndex.length > 0 ? (
											availableForIndex.map((availableField) => (
												<button
													key={`${availableField.objectTable}.${availableField.fieldName}`}
													onClick={() => {
														updateSortField(index, {
															objectTable: availableField.objectTable,
															fieldName: availableField.fieldName,
														});
														setOpenFieldDropdown(null);
													}}
													className={`w-full p-2 text-left ${colors.classes.bgPrimaryHover} border-b ${colors.classes.borderDefault} last:border-b-0`}
												>
													<span
														className={`text-xs ${colors.classes.textPrimary} font-medium`}
													>
														{availableField.objectLabel}:{' '}
														{availableField.fieldLabel}
													</span>
												</button>
											))
										) : (
											<div className="p-3">
												<p
													className={`text-xs ${colors.classes.textSecondary} text-center`}
												>
													No more fields available
												</p>
												<p
													className={`text-[10px] ${colors.classes.textSecondary} text-center mt-1`}
												>
													All fields are already in use
												</p>
											</div>
										);
									})()}
								</div>
							)}
						</div>

						{/* Sort Direction Buttons */}
						<div className="flex gap-1">
							<button
								onClick={() => updateSortField(index, { direction: 'ASC' })}
								className={`
                  px-3 py-1.5 rounded text-xs font-medium
                  ${
										sortField.direction === 'ASC'
											? `${colors.classes.bgPrimary} text-white`
											: `${colors.classes.bgSecondary} ${colors.classes.textSecondary} ${colors.classes.bgPrimaryHover}`
									}
                `}
							>
								ASC
							</button>
							<button
								onClick={() => updateSortField(index, { direction: 'DESC' })}
								className={`
                  px-3 py-1.5 rounded text-xs font-medium
                  ${
										sortField.direction === 'DESC'
											? 'bg-blue-600 text-white'
											: `${colors.classes.bgTertiary} ${colors.classes.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`
									}
                `}
							>
								DESC
							</button>
						</div>

						{/* Delete Button */}
						<button
							onClick={() => deleteSortField(index)}
							className={`ml-2 p-1 ${colors.classes.bgPrimaryHover} rounded`}
						>
							<Icon
								icon={faTrash}
								size="sm"
								className={colors.classes.textSecondary}
							/>
						</button>
					</div>
				)}
				onReorder={reorderSortFields}
			/>
		</div>
	);
}
